import React, { useCallback, useContext, useState, useMemo } from 'react';
import OfferContext from './OfferContext';
import { useLoading } from '../../utils/LoadingUtil';
import offerModel from '../../model/setting/Offer.model';
import {
  getOffers,
  createOffer,
  updateOffer,
  deleteOffer,
} from '../../services/OfferService';

const OfferContextProvider = ({ children }) => {
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { offer, setOffer, initialState } = offerModel();
  const [error, setError] = useState(null);
  const [offerRes, setOfferRes] = useState([]);

  const fetchOffers = useCallback(async () => {
    startLoading();
    try {
      const offers = await getOffers();
      setOfferRes(offers);
    } catch (error) {
      setError(error);
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  const handleCreateOffer = useCallback(
    async (offerData, showOffersForm) => {
      startLoading();
      try {
        await createOffer(offerData);
        showOffersForm(); // Close the form after successful submission
        fetchOffers(); // Refresh the offers list
      } catch (error) {
        setError(error);
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchOffers],
  );

  const handleUpdateOffer = useCallback(
    async (offerId, offerData, showOffersForm) => {
      startLoading();
      try {
        await updateOffer(offerId, offerData);
        showOffersForm(); // Close the form after successful submission
        fetchOffers(); // Refresh the offers list
      } catch (error) {
        setError(error);
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchOffers],
  );

  const handleDeleteOffer = useCallback(
    async (offerId) => {
      startLoading();
      try {
        await deleteOffer(offerId);
        fetchOffers(); // Refresh the offers list
      } catch (error) {
        setError(error);
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchOffers],
  );

  const contextValue = useMemo(
    () => ({
      getOffers: fetchOffers,
      createOffer: handleCreateOffer,
      updateOffer: handleUpdateOffer,
      deleteOffer: handleDeleteOffer,
      isLoading,
      error,
      setError,
      offerRes,
      offer,
      setOffer,
      initialState,
    }),
    [
      fetchOffers,
      handleCreateOffer,
      handleUpdateOffer,
      handleDeleteOffer,
      isLoading,
      error,
      offerRes,
      offer,
      setOffer,
      initialState,
    ],
  );

  return (
    <OfferContext.Provider value={contextValue}>
      {children}
    </OfferContext.Provider>
  );
};

export default OfferContextProvider;
export const useOffer = () => useContext(OfferContext);
